/**
 * The app's sidebar: the design system's Sidebar (src/ui), filled from the
 * route table. Harvey's frame, day 33: the wordmark, Dashboard, the four
 * sections with their features under them, then History, Subscriptions and
 * Settings at the foot, and the student, whose row opens the theme and Log out.
 * Recents left the sidebar on 30 October 2026 (Mohit): History lists them all.
 *
 * Two levels and no third: routes.ts holds the list, test/routes.test.ts
 * holds it to PRD v3.2 section 5.
 */

import type { PlanSummary, Profile } from "@ls/shared";
import { FREE_PLAN, tierOf } from "@ls/shared/catalogue";
import { Icon, Select, Sidebar as Frame, Wordmark, type SideItem } from "../ui";
import { SIDEBAR, routeById, type RouteId } from "./routes";
import type { ThemeChoice } from "./theme";

export interface SidebarProps {
  readonly active: RouteId | null;
  readonly profile: Profile;
  readonly plan: PlanSummary;
  readonly theme: ThemeChoice;
  /** Off screen or folded away: taken out of the tab order and the accessibility tree. */
  readonly hidden: boolean;
  readonly onToggle: () => void;
  /** A link in the sidebar was followed, including one to the page already showing. */
  readonly onFollow: () => void;
  readonly onTheme: (choice: ThemeChoice) => void;
  readonly onSignOut: () => void;
}

/** The sidebar's rows, in order, as the design system draws them. */
export function sideItems(): SideItem[] {
  const link = (id: RouteId) => ({ id, label: routeById(id).title, href: routeById(id).path });
  return [
    ...SIDEBAR.top.map((row) => ({ kind: "top" as const, icon: row.icon, ...link(row.id) })),
    ...SIDEBAR.sections.map((section) => ({ kind: "section" as const, label: section.label, icon: section.icon, items: section.ids.map(link) })),
  ];
}

/** "Asha Rao" gives "AR"; an address gives its first letter. */
export function initialsOf(profile: Profile): string {
  const words = (profile.name ?? "").trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0], words[words.length - 1]] : words.length === 1 ? [words[0]] : [profile.email];
  return letters.map((word) => (word ?? "").charAt(0).toUpperCase()).join("");
}

/**
 * The plan and its Credits, under the student's name: "Free plan", or "Elite ·
 * 90 Credits". An intern's 30 days are Elite, and read as Elite. Until the
 * Credit ledger (day 61) the Credits are the plan's own.
 */
export function planLine(plan: PlanSummary): string {
  if (plan.type === "free" || !plan.tier) return `${FREE_PLAN.name} plan`;
  return `${tierOf(plan.tier).name} · ${creditsWords(plan.credits)}`;
}

/** "1 Credit", "90 Credits". */
export function creditsWords(credits: number): string {
  return `${credits.toLocaleString("en-GB")} ${credits === 1 ? "Credit" : "Credits"}`;
}

const THEMES: readonly { value: ThemeChoice; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

export function Sidebar(props: SidebarProps) {
  const foot = SIDEBAR.foot.map((row) => ({ id: row.id, icon: row.icon, label: routeById(row.id).title, href: routeById(row.id).path }));
  return (
    <Frame
      id="sidebar"
      brand={<Wordmark />}
      items={sideItems()}
      foot={foot}
      current={props.active}
      hidden={props.hidden}
      onClose={props.onToggle}
      onFollow={props.onFollow}
      me={{
        initials: initialsOf(props.profile),
        name: props.profile.name ?? props.profile.email,
        detail: planLine(props.plan),
        menu: (
          <>
            <div class="menu-row">
              <label for="menu-theme">Theme</label>
              <Select id="menu-theme" size="sm" up options={THEMES} value={props.theme} onChange={(choice) => props.onTheme(choice as ThemeChoice)} />
            </div>
            <hr class="menu-line" />
            <button type="button" class="menu-item" onClick={props.onSignOut}>
              <Icon name="leave" />
              Log out
            </button>
          </>
        ),
      }}
    />
  );
}
